// Implementação do serviço de recomendações
// Usa a fachada RecommendationFacade para acessar o sistema de recomendação

import { Game, Recommendation as SharedRecommendation } from '../types';
import { RecommendationFacade } from './RecommendationFacade';
import { supabase } from './supabase';
import { recommendationService as recommendationServiceInstance } from './recommendation';

// Interface para organizar as recomendações por tipo
export interface RecommendationsData {
  popular: SharedRecommendation[];
  personalized: SharedRecommendation[];
}

// Interface para opções de recomendação
export interface RecommendationOptions {
  forcePersonalized?: boolean; // Forçar uso de algoritmos personalizados
  limit?: number; // Número máximo de recomendações
}

// Função para obter recomendações
export async function getRecommendations(
  userId?: string, 
  options?: RecommendationOptions
): Promise<RecommendationsData> {
  try {
    if (!userId) {
      console.error('getRecommendations: ID de usuário não fornecido');
      return { popular: [], personalized: [] };
    }

    // Criar requisição para a API
    const request = {
      userId,
      forcePersonalized: options?.forcePersonalized || false
    };

    console.log(`[recommendationService] Solicitando recomendações para usuário ${userId} com forcePersonalized=${request.forcePersonalized}`);

    // Usar a instância da fachada com os novos parâmetros
    const facadeResponse = await recommendationServiceInstance.getRecommendations(
      userId, 
      request.forcePersonalized
    );
    
    // Assumir que a facade já retorna o tipo correto (RecommendationsResponse com SharedRecommendation)
    return facadeResponse;

  } catch (error) {
    console.error('Erro ao obter recomendações:', error);
    return { popular: [], personalized: [] };
  }
}

// Função para marcar recomendação como visualizada
export async function markRecommendationAsViewed(recommendationId: string): Promise<boolean> {
  try {
    if (!recommendationId) {
      console.error('markRecommendationAsViewed: ID de recomendação não fornecido');
      return false;
    }

    await recommendationServiceInstance.markRecommendationAsViewed(recommendationId);
    return true;
  } catch (error) {
    console.error('Erro ao marcar recomendação como visualizada:', error);
    return false;
  }
}

// Função para fornecer feedback sobre recomendação
// Agora retorna a recomendação atualizada ou null
export async function giveRecommendationFeedback(
  recommendationId: string, 
  liked: boolean | null, // Aceita boolean ou null
  temporaryRecommendation?: SharedRecommendation | null // Parâmetro opcional para fallback
): Promise<SharedRecommendation | null> { // Mudar tipo de retorno
  try {
    if (!recommendationId) {
      console.error('giveRecommendationFeedback: ID de recomendação não fornecido');
      return null;
    }

    // Chamar diretamente a Facade
    const updatedRec = await recommendationServiceInstance.giveRecommendationFeedback(
      recommendationId, 
      liked, 
      temporaryRecommendation 
    );

    return updatedRec;

  } catch (error) {
    console.error('Erro ao dar feedback para recomendação:', error);
    return null;
  }
}

// Função para obter recomendações com feedback
export async function getFeedbackRecommendations(userId?: string, liked?: boolean): Promise<SharedRecommendation[]> {
  try {
    if (!userId) {
      console.error('getFeedbackRecommendations: ID de usuário não fornecido');
      return [];
    }

    if (liked === undefined) {
      console.error('getFeedbackRecommendations: Parâmetro liked não fornecido');
      return [];
    }

    const recommendations = await recommendationServiceInstance.getFeedbackRecommendations({
      userId,
      liked
    });
    return recommendations;
  } catch (error) {
    console.error('Erro ao obter recomendações com feedback:', error);
    return [];
  }
}

// Função para remover feedback
export async function removeFeedback(recommendationId: string): Promise<boolean> {
  try {
    if (!recommendationId) {
      console.error('removeFeedback: ID de recomendação não fornecido');
      return false;
    }

    const updatedRec = await recommendationServiceInstance.removeFeedback(recommendationId);
    return !!updatedRec;
  } catch (error) {
    console.error('Erro ao remover feedback:', error);
    return false;
  }
}

// Instância da Facade (assumindo que já existe ou é criada aqui)
const recommendationService = new RecommendationFacade(); 